import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

// MUI imports
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, CssBaseline, Toolbar, Typography, useMediaQuery } from '@mui/material';

// Project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import Customization from '../Customization';
import navigation from 'menu-items';
import { drawerWidth } from 'store/constant';
import { SET_MENU } from 'store/actions';

// Icon
import { IconChevronRight } from '@tabler/icons';

// Styled Main Content
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    ...theme.typography.mainContent,
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen
    }),
    marginLeft: open ? 0 : -(drawerWidth - 20),
    width: open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${drawerWidth - 20}px)`,

    [theme.breakpoints.down('md')]: {
        marginLeft: 0,
        width: '100%',
        padding: '16px'
    },
    [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
        width: '100%',
        padding: '16px'
    }
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('lg'));

    // Redux Drawer state
    const leftDrawerOpened = useSelector((state) => state.customization.opened);
    const dispatch = useDispatch();

    // Toggle drawer manually
    const handleLeftDrawerToggle = () => {
        dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
    };

    // Set drawer open/close only on initial mount
    useEffect(() => {
        dispatch({ type: SET_MENU, opened: !matchDownMd });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <CssBaseline />

            {/* AppBar Header */}
            <AppBar
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    bgcolor: theme.palette.background.default,
                    zIndex: theme.zIndex.drawer + 1
                }}
            >
                <Toolbar>
                    <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
                </Toolbar>
            </AppBar>

            <Box sx={{ display: 'flex', flex: 1 }}>
                {/* Sidebar */}
                <Sidebar drawerOpen={leftDrawerOpened} drawerToggle={handleLeftDrawerToggle} />

                {/* Main Content Area */}
                <Main theme={theme} open={leftDrawerOpened}>
                    <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                    <Outlet />
                </Main>
            </Box>

            <Box
                component="footer"
                sx={{
                    bgcolor: '#1B3A57',
                    color: 'white',
                    py: 4,
                    px: { xs: 2, sm: 4 },
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',

                    position: 'relative'
                }}
            >
                {/* Center Section - Branding */}
                <Typography
                    variant="body2"
                    sx={{ textAlign: 'center', color: 'white', position: 'absolute', left: 0, right: 0 }}
                >
                    © {new Date().getFullYear()} <Box component="span" sx={{ color: '#FF5252', fontWeight: 600 }}>TruCred</Box>. All Rights Reserved.
                </Typography>

                {/* Right Section - Links */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 3,
                        fontSize: '0.9rem',
                        position: 'absolute',
                        right: { xs: '50%', sm: 20 },
                        transform: { xs: 'translateX(50%)', sm: 'none' }
                    }}
                >
                    <Typography
                        component="a"
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                        Privacy Policy
                    </Typography>
                    <Typography
                        component="a"
                        href="/termsofUse"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                        Terms of Service
                    </Typography>
                </Box>
            </Box>



            <Customization />
        </Box>
    );

};

export default MainLayout;
